import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Progress } from '../components/ui/progress';
import { toast } from 'sonner';
import { ArrowLeft, CheckCircle, Clock, Loader2, Sparkles, Trophy, Zap } from 'lucide-react';
import CabinCrewLayout from '../layouts/CabinCrewLayout';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const GrammarExerciseDuolingo = () => {
  const navigate = useNavigate();
  const { level, topic } = useParams();
  const [session, setSession] = useState(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [answers, setAnswers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [timeLeft, setTimeLeft] = useState(600);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  
  useEffect(() => {
    startExercise();
  }, [level, topic]);
  
  useEffect(() => {
    if (session && timeLeft > 0) {
      const timer = setInterval(() => {
        setTimeLeft(prev => prev - 1);
      }, 1000);
      return () => clearInterval(timer);
    } else if (timeLeft === 0) {
      handleSubmit();
    }
  }, [session, timeLeft]);
  
  const startExercise = async () => {
    try {
      // Level ve topic parametrelerini doğru gönder
      const response = await axios.post(
        `${API}/exercises/grammar/start`,
        {},
        { 
          params: { topic, level, count: 10 },
          withCredentials: true 
        }
      );
      setSession(response.data);
      setAnswers(new Array(response.data.exercises.length).fill(null));
    } catch (error) {
      console.error('Exercise start error:', error);
      toast.error('Egzersiz başlatılamadı');
      navigate('/exercises');
    } finally {
      setLoading(false);
    }
  };
  
  const handleAnswer = (answer) => {
    setSelectedAnswer(answer);
    const newAnswers = [...answers];
    newAnswers[currentIndex] = answer;
    setAnswers(newAnswers);
    
    // Auto advance after 500ms
    setTimeout(() => {
      if (currentIndex < session.exercises.length - 1) {
        setCurrentIndex(currentIndex + 1);
        setSelectedAnswer(null);
      }
    }, 500);
  };
  
  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      const formattedAnswers = answers.map((answer, idx) => ({
        exercise_id: idx,
        answer: answer !== null ? answer : 0
      }));
      
      await axios.post(
        `${API}/exercises/submit`,
        {
          session_id: session.session_id,
          answers: formattedAnswers
        },
        { withCredentials: true }
      );
      
      navigate(`/exercises/results/${session.session_id}`);
    } catch (error) {
      toast.error('Gönderim başarısız');
    } finally {
      setSubmitting(false);
    }
  };
  
  if (loading) {
    return (
      <CabinCrewLayout>
        <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50">
          <div className="text-center">
            <div className="relative">
              <div className="w-24 h-24 border-8 border-blue-200 border-t-blue-600 rounded-full animate-spin mx-auto"></div>
              <div className="absolute inset-0 flex items-center justify-center">
                <Sparkles className="w-12 h-12 text-blue-600 animate-pulse" />
              </div>
            </div>
            <p className="mt-6 text-xl font-bold text-gray-700 animate-pulse">Sorular hazırlanıyor...</p>
            <p className="text-sm text-gray-500">🎯 Seviye: {level} | Konu: {topic}</p>
          </div>
        </div>
      </CabinCrewLayout>
    );
  }
  
  if (!session) return null;
  
  const currentExercise = session.exercises[currentIndex];
  const answeredCount = answers.filter(a => a !== null).length;
  const progressPercentage = (answeredCount / session.exercises.length) * 100;
  
  return (
    <CabinCrewLayout>
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50 py-8">
      <div className="container mx-auto px-4 max-w-3xl">
        {/* Top Bar */}
        <div className="flex items-center justify-between mb-6">
          <Button
            variant="outline"
            onClick={() => navigate('/exercises')}
            className="border-2 border-gray-300 hover:border-gray-500"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Geri
          </Button>
          
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2 px-4 py-2 bg-orange-100 border-2 border-orange-300 rounded-full">
              <Clock className="w-5 h-5 text-orange-600" />
              <span className="text-lg font-black text-orange-600">
                {Math.floor(timeLeft / 60)}:{(timeLeft % 60).toString().padStart(2, '0')}
              </span>
            </div>
          </div>
        </div>
        
        {/* Progress Bar */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-bold text-gray-600">İlerleme</span>
            <span className="text-sm font-bold text-blue-600">{answeredCount}/{session.exercises.length}</span>
          </div>
          <Progress value={progressPercentage} className="h-3 bg-gray-200" />
        </div>
        
        {/* Question Card */}
        <Card className="border-4 border-white bg-white shadow-2xl mb-6">
          <CardContent className="p-6 sm:p-8">
            <div className="mb-6">
              <div className="inline-block px-4 py-2 bg-blue-100 border-2 border-blue-300 rounded-full mb-4">
                <span className="text-sm font-black text-blue-600">Soru {currentIndex + 1}/{session.exercises.length}</span>
              </div>
              {/* Question text - properly displayed */}
              {currentExercise.question ? (
                <h2 className="text-xl sm:text-2xl lg:text-3xl font-black text-gray-900 mb-2 leading-relaxed">
                  {currentExercise.question}
                </h2>
              ) : (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-2">
                  <p className="text-amber-700">Soru yükleniyor...</p>
                </div>
              )}
            </div>
            
            <div className="space-y-3">
              {currentExercise.options?.map((option, idx) => {
                const isSelected = answers[currentIndex] === idx;
                const isCurrentlyClicked = selectedAnswer === idx;
                
                return (
                  <button
                    key={idx}
                    onClick={() => handleAnswer(idx)}
                    disabled={submitting}
                    className={`w-full p-4 sm:p-6 rounded-2xl border-4 transition-all text-left font-bold text-base sm:text-lg ${
                      isSelected
                        ? 'border-blue-500 bg-blue-100 shadow-xl scale-[1.02]'
                        : 'border-gray-200 hover:border-blue-300 hover:bg-blue-50 hover:scale-[1.01]'
                    } ${
                      isCurrentlyClicked ? 'animate-pulse' : ''
                    }`}
                  >
                    <div className="flex items-center gap-3 sm:gap-4">
                      <div className={`w-7 h-7 sm:w-8 sm:h-8 rounded-full border-4 flex items-center justify-center shrink-0 ${
                        isSelected
                          ? 'border-blue-500 bg-blue-500'
                          : 'border-gray-300 bg-white'
                      }`}>
                        {isSelected && <CheckCircle className="w-4 h-4 sm:w-5 sm:h-5 text-white" />}
                      </div>
                      <span className="flex-1 break-words">{option}</span>
                    </div>
                  </button>
                );
              }) || (
                <div className="text-center text-gray-500 py-4">
                  Seçenekler yükleniyor...
                </div>
              )}
            </div>
          </CardContent>
        </Card>
        
        {/* Navigation Dots */}
        <div className="flex justify-center gap-2 mb-6">
          {session.exercises.map((_, idx) => (
            <button
              key={idx}
              onClick={() => setCurrentIndex(idx)}
              className={`transition-all ${
                idx === currentIndex
                  ? 'w-4 h-4 bg-blue-500 rounded-full'
                  : answers[idx] !== null
                  ? 'w-3 h-3 bg-green-500 rounded-full'
                  : 'w-3 h-3 bg-gray-300 rounded-full'
              }`}
            />
          ))}
        </div>
        
        {/* Bottom Actions */}
        <div className="flex justify-center gap-4">
          {currentIndex === session.exercises.length - 1 ? (
            <Button
              onClick={handleSubmit}
              disabled={submitting}
              size="lg"
              className="bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white font-black text-xl px-12 py-6 rounded-2xl shadow-2xl"
            >
              {submitting ? (
                <>
                  <Loader2 className="w-6 h-6 mr-2 animate-spin" />
                  Gönderiliyor...
                </>
              ) : (
                <>
                  <Trophy className="w-6 h-6 mr-2" />
                  Tamamla! 🎉
                </>
              )}
            </Button>
          ) : (
            <Button
              onClick={() => {
                setCurrentIndex(currentIndex + 1);
                setSelectedAnswer(null);
              }}
              size="lg"
              className="bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white font-black text-xl px-12 py-6 rounded-2xl shadow-2xl"
            >
              Sonraki →
            </Button>
          )}
        </div>
      </div>
    </div>
    </CabinCrewLayout>
  );
};

export default GrammarExerciseDuolingo;